import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Select } from 'common-src/components/base';
import {
  AthenaPracticeOptions,
  createPatientOnAthena,
} from 'common-src/models/PatientIntegrationsAthena';
import { ColorsNew } from 'common-src/styles';

import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import { AthenaDepartmentIdOptions, initialState } from './constants';
import styles from './CreateAthenaPatientPopup.module.scss';
import { catchErrors } from './helpers';

const CreateAthenaPatientPopup = ({ onClose, patientId }) => {
  const dispatch = useDispatch();

  const [data, setData] = useState(initialState);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState('');

  const { errors, setErrors, renderButtons } = usePopup();

  const onChangeHandler = (field, value) => {
    setErrors((prev) => ({ ...prev, [field]: '' }));
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const onSubmitHandler = async (loadingCallback, successCallback, errorCallback) => {
    const { errors, hasErrors } = catchErrors(data);
    if (hasErrors) {
      setErrors(errors);
      return;
    }

    loadingCallback();
    setIsProcessing(true);

    const results = [];

    // Step 1: Create patient on Athena
    setCurrentStep('Creating patient...');
    let athenaPatientId = null;

    const createResult = await new Promise((resolve) => {
      dispatch(
        createPatientOnAthena(patientId, data.practiceId, data.departmentId, {
          successBlock: (response) => {
            athenaPatientId = response?.patientid;
            resolve({ step: 'Create Patient', success: true, athenaPatientId });
          },
          errorBlock: (err) => resolve({ step: 'Create Patient', success: false, error: err }),
        }),
      );
    });
    results.push(createResult);

    if (!createResult.success) {
      setIsProcessing(false);
      errorCallback(`Failed to create patient: ${createResult.error}`);
      return;
    }
    setIsProcessing(false);
    setCurrentStep('');

    // Build result summary
    const successful = results.filter((r) => r.success).map((r) => r.step);
    const failed = results.filter((r) => !r.success);

    let message = `Athena Patient ID: ${athenaPatientId}\n\n`;
    message += `Successful: ${successful.join(', ') || 'None'}\n`;

    if (failed.length > 0) {
      message += `\nFailed:\n${failed.map((f) => `- ${f.step}: ${f.error}`).join('\n')}`;
    }

    if (failed.length === 0) {
      successCallback(message);
    } else if (successful.length > 0) {
      successCallback(message);
    } else {
      errorCallback(message);
    }

    onClose(true);
  };

  return (
    <BasePopup id="create-athena-patient" open onClose={onClose} title="Create Patient on Athena">
      <div className={[styles.container, 'flex-column', 'gap-16'].join(' ')}>
        <Select
          id="practice-id"
          label="Practice ID"
          options={AthenaPracticeOptions}
          onChange={(op) => onChangeHandler('practiceId', op?.value ?? null)}
          value={AthenaPracticeOptions.find((op) => op.value === data.practiceId)}
          placeholder="Select practice ID"
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.practiceId}
          disabled={isProcessing}
        />
        <Select
          id="department-id"
          label="Department"
          options={AthenaDepartmentIdOptions}
          onChange={(op) => onChangeHandler('departmentId', op?.value ?? null)}
          value={AthenaDepartmentIdOptions.find((op) => op.value === data.departmentId)}
          placeholder="Select department"
          requiredColor={ColorsNew.mediumDarkRed}
          errorText={errors.departmentId}
          disabled={isProcessing}
        />
        {isProcessing && currentStep && (
          <div className={styles.processingStatus}>
            <span>{currentStep}</span>
          </div>
        )}
      </div>
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        onClose,
        onSubmit: onSubmitHandler,
        isSubmitEnabled: !Object.values(errors).some((e) => e) && !isProcessing,
        submitButtonText: isProcessing ? 'Processing...' : 'Create Patient',
      })}
    </BasePopup>
  );
};

CreateAthenaPatientPopup.propTypes = {
  onClose: PropTypes.func,
  patientId: PropTypes.number,
};

export default CreateAthenaPatientPopup;
